import React, { Suspense, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ResourceType, type WorkflowArtifactData } from '@cotera/api';
import type { ArtifactRendererProps } from './types';
import { registerArtifactRenderer } from './registry';
import {
  WorkflowArtifactPage,
  WorkflowPageWithFetchedData,
} from '@cotera/client/app/v1/workflow/workflow.page';
import { Text } from '@cotera/client/app/components/ui';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { ChatArtifactAccessGate } from './chat-artifact-access-gate';
import { parseChatSeedFolderId } from '@cotera/client/app/v1/chat-seed/url';
import { parseSeedTrigger } from '@cotera/client/app/v1/workflow/chat-seed/seed-trigger';

import type {
  WorkflowEditorTab,
  WorkflowSeedNewDatasetTrigger,
} from '@cotera/client/app/v1/workflow/workflow.model';

function workflowArtifactTabToEditorTab(
  tab: WorkflowArtifactData['tab']
): WorkflowEditorTab {
  return tab === 'runs' ? 'runs' : 'graph';
}

type WorkflowPayload = WorkflowArtifactData & {
  /**
   * A brand-new dataset trigger to seed onto a fresh draft, set when this
   * workflow is opened as a sibling artifact ("Add automation" on a dataset)
   * rather than through a seed URL. Its presence marks the workflow as an
   * unsaved draft — so, unlike a bare `isNew` payload flag (which old chats
   * persist for long-saved workflows), it needs no server round-trip to confirm.
   */
  seedNewDatasetTrigger?: WorkflowSeedNewDatasetTrigger;
  /**
   * A brand-new, unsaved workflow opened as a sibling artifact ("Create new
   * workflow" in the add-node picker). Only ever set by an in-session
   * `openArtifact` — never seeded into a chat the way `isNew` is — so it marks
   * a draft outright and needs no server round-trip to confirm.
   */
  isNewDraft?: boolean;
};

const WorkflowArtifactSkeleton: React.FC = () => (
  <div className="h-full w-full bg-background flex flex-col min-h-0 overflow-hidden">
    <div className="flex-1 min-h-0 overflow-hidden p-5">
      <div className="h-full w-full rounded bg-coco-accent animate-pulse" />
    </div>
  </div>
);

const WorkflowArtifactRenderer: React.FC<ArtifactRendererProps> = ({
  artifact,
}) => {
  const [searchParams] = useSearchParams();
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const payload = artifact.payload as WorkflowPayload | null;
  const workflowId = payload?.workflowId ?? artifact.artifactId;
  // Read once, at mount. The seed URL is authoritative while it says `new` — the
  // workflow may not exist yet — but the marker is cleared the moment the
  // workflow is saved, and letting that flip mid-session would tear down the
  // editor the user is working in and remount it from the server.
  const [isNewFromUrl] = useState(
    () =>
      searchParams.get('new') === '1' &&
      searchParams.get('artifactId') === workflowId
  );
  // Captured at mount for the same reason. This artifact registers itself on the
  // conversation, and that round-trip swaps our seeded payload for a bare
  // resource descriptor; reading the seed live would then drop the pre-bound
  // trigger and remount the editor from the (nonexistent) server copy.
  const [seedNewDatasetTrigger] = useState(
    () => (artifact.payload as WorkflowPayload | null)?.seedNewDatasetTrigger
  );
  // Captured at mount for the same reason as the seed trigger above.
  const [isNewDraftFromPayload] = useState(
    () => (artifact.payload as WorkflowPayload | null)?.isNewDraft === true
  );
  // Both markers are only ever set by an in-session sibling-artifact open, so
  // either definitively marks a fresh, unsaved draft — no stale-payload server
  // check needed.
  const isDraftFromPayload =
    seedNewDatasetTrigger !== undefined || isNewDraftFromPayload;
  const hasWorkflowId = workflowId !== undefined && workflowId !== '';
  // A payload `isNew` only describes the moment the chat was seeded. Chats
  // created before that client-only flag stopped being persisted still carry it
  // for workflows saved long since, so without a live seed URL backing it up,
  // check the server rather than reopening the new-workflow flow on a blank
  // draft. Shares the access gate's query, so the resolved case costs nothing.
  const shouldResolveStalePayload =
    payload?.isNew === true &&
    !isNewFromUrl &&
    !isDraftFromPayload &&
    hasWorkflowId;
  const persisted = useQuery({
    queryKey: queryKeys.resource.get({
      orgId,
      type: ResourceType.Workflow,
      id: workflowId,
    }),
    queryFn: () =>
      client.resource.get({ type: ResourceType.Workflow, id: workflowId }),
    staleTime: 0,
    enabled: shouldResolveStalePayload,
  });
  const initialEditorTab = workflowArtifactTabToEditorTab(payload?.tab);
  const folderId = parseChatSeedFolderId(searchParams);
  // A trigger this workflow was seeded from ("Create new workflow" under a
  // trigger card) — pre-bound onto the new draft so it opens with the trigger
  // on the graph rather than blank.
  const seedTrigger = parseSeedTrigger(searchParams);

  if (!hasWorkflowId) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background p-4">
        <Text.P className="text-muted-foreground">Workflow not found</Text.P>
      </div>
    );
  }

  if (shouldResolveStalePayload && persisted.data === undefined) {
    return <WorkflowArtifactSkeleton />;
  }

  const isNew =
    isNewFromUrl ||
    isDraftFromPayload ||
    (shouldResolveStalePayload && persisted.data?.isErr() === true);

  if (isNew) {
    return (
      <WorkflowArtifactPage
        id={workflowId}
        isNew
        folderId={folderId}
        seedTrigger={seedTrigger}
        seedNewDatasetTrigger={seedNewDatasetTrigger}
        initialEditorTab={initialEditorTab}
      />
    );
  }

  return (
    <ChatArtifactAccessGate
      type={ResourceType.Workflow}
      id={workflowId}
      artifactType="workflow"
      fallback={<WorkflowArtifactSkeleton />}
    >
      <Suspense fallback={<WorkflowArtifactSkeleton />}>
        <WorkflowPageWithFetchedData
          id={workflowId}
          initialEditorTab={initialEditorTab}
        />
      </Suspense>
    </ChatArtifactAccessGate>
  );
};

registerArtifactRenderer('workflow', WorkflowArtifactRenderer);
